import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { useExam } from "@/context/ExamContext";
import TopicHeatmap from "@/components/TopicHeatmap";
import { useWhiteLabel, PoweredByBadge } from "@/contexts/WhiteLabelContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { API_BASE } from "@/App";
import PasswordSetupPrompt from "@/components/PasswordSetupPrompt";
import PrepOSStudentWidget from "@/components/PrepOSStudentWidget";
import { getApiBaseUrl } from "@/services/api";
import useHeartbeat from "@/hooks/useHeartbeat";
import { toast } from "sonner";
import { 
  ArrowRight, ChevronDown, ChevronUp, Settings, Award, Crown,
  Target, PenTool, AlertTriangle, Shield, Clock, Play,
  Brain, Newspaper, Sparkles, TrendingUp, Calendar, Gift,
  Flame, Zap, BookOpen, BarChart3, Trophy, Star, Rocket,
  CheckCircle, Timer, ChevronRight, Bell, Rss,
  GraduationCap, FileText, Lightbulb, TrendingDown, Circle, HelpCircle
} from "lucide-react";

/**
 * CLARIAS SuperKalam-Inspired Vibrant Dashboard
 * 
 * Features:
 * 1. Animated Header with Streak
 * 2. Daily Learning Targets
 * 3. Quick Stats Cards
 * 4. Today's Command (Hero)
 * 5. Primary Hubs (Prelims/Mains)
 * 6. Weak Area Alert
 * 7. Risk Meters
 * 8. Upcoming Events
 * 9. Explore Features
 */

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isWhiteLabel, config, getInstituteName } = useWhiteLabel();
  // Exam-track awareness (UPSC / SI). SI reuses shared engines under its own label.
  const { getDaysToExam: examDaysFromCtx, isSI } = useExam();

  // Fire a daily heartbeat so streak credits a "studied today" even if the
  // user only opened the dashboard. Idempotent on backend (one row / IST day).
  useHeartbeat({ activity: "dashboard_open", module: "dashboard" });
  const [loading, setLoading] = useState(true);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [commandStatus, setCommandStatus] = useState(null);
  const [intelligenceReport, setIntelligenceReport] = useState(null);
  const [exploreExpanded, setExploreExpanded] = useState(false);
  const [digestSummary, setDigestSummary] = useState(null);
  const [streakData, setStreakData] = useState({ current_streak: 0, longest_streak: 0 });
  const [dailyTargets, setDailyTargets] = useState({
    mcqs: { target: 10, completed: 0 },
    answers: { target: 1, completed: 0 },
    ca: { target: 1, completed: 0 }
  });
  
  // Parent portal state removed - now open access

  // Exam-scoped PRO. A UPSC PRO purchase does NOT unlock SI (and vice-versa),
  // so in SI context we only treat the user as PRO if they hold the SI entitlement.
  const isPro = user?.role === "ADMIN" || user?.is_admin || (isSI ? (user?.pro_exams || []).includes("SI") : user?.role === "PRO");
  
  // Helper function to format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Recently";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  // Motivational quotes with authors
  const quotes = [
    { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
    { text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
    { text: "Hard work beats talent when talent doesn't work hard.", author: "Tim Notke" },
    { text: "One correct decision per day beats 10 random tasks.", author: "CLARIAS" },
    { text: "Clarity beats confusion. Always.", author: "CLARIAS Philosophy" },
    { text: "CSAT is qualifying. Don't gamble with it.", author: "UPSC Wisdom" }
  ];
  const [currentQuote] = useState(quotes[Math.floor(Math.random() * quotes.length)]);
  
  // Get time-based greeting with emoji
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: "Good morning", emoji: "🌅" };
    if (hour < 17) return { text: "Good afternoon", emoji: "☀️" };
    return { text: "Good evening", emoji: "🌙" };
  };
  
  const greeting = getGreeting();
  const firstName = user?.name?.split(" ")[0] || "Aspirant";

  // Next UPSC Prelims date with auto-rollover.
  // If the configured date is in the past, automatically jump to next year.
  // This prevents the countdown from showing negative days after exam day.
  const getNextPrelimsDate = () => {
    const baseDate = new Date('2027-05-23'); // UPSC Prelims 2027
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exam = new Date(baseDate);
    exam.setHours(0, 0, 0, 0);
    // If even the base date is past, roll forward year-by-year
    while (exam < today) {
      exam.setFullYear(exam.getFullYear() + 1);
    }
    return exam;
  };
  const PRELIMS_DATE = getNextPrelimsDate();
  const getDaysToExam = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.ceil((PRELIMS_DATE - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, days); // never show negative
  };
  // Countdown driven by the selected exam (SI → its date); fallback to UPSC.
  const daysToExam = examDaysFromCtx() ?? getDaysToExam();

  // Calculate weeks and months to exam
  const weeksToExam = Math.floor(daysToExam / 7);
  const monthsToExam = Math.floor(daysToExam / 30);

  useEffect(() => {
    const shouldShowPrompt = localStorage.getItem("show_password_prompt");
    if (shouldShowPrompt === "true") {
      setTimeout(() => setShowPasswordPrompt(true), 1500);
    }
  }, []);

  // Parent link approval removed - auto-approved by admin

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    
    const fetchAllData = async () => {
      const token = localStorage.getItem("token");
      
      try {
        // Fetch both dashboard-all and prep-os stats in parallel
        const [dashRes, prepOsRes] = await Promise.all([
          fetch(`${API_BASE}/command/dashboard-all`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          }),
          fetch(`${API_BASE}/prep-os/dashboard-stats`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          }).catch(() => null) // Don't fail if prep-os fails
        ]);
        
        if (!dashRes.ok) throw new Error("Failed to fetch dashboard data");
        const data = await dashRes.json();
        
        // Get PrepOS data for consistent streak
        let prepOsData = null;
        if (prepOsRes && prepOsRes.ok) {
          prepOsData = await prepOsRes.json();
        }
        
        if (isMounted) {
          // Command Status
          if (data.command_status) setCommandStatus(data.command_status);
          
          // Intelligence Report
          if (data.intelligence_report) setIntelligenceReport(data.intelligence_report);
          
          // Digest Summary
          if (data.digest_summary) setDigestSummary(data.digest_summary);
          
          // Streak Data - prefer PrepOS metrics for consistency
          if (prepOsData?.metrics?.streak_days !== undefined) {
            setStreakData({ 
              current_streak: prepOsData.metrics.streak_days, 
              longest_streak: prepOsData.metrics.streak_days 
            });
          } else if (data.streak) {
            setStreakData(data.streak);
          }
          
          // Daily Targets from today_targets
          if (data.today_targets?.completion_details) {
            const details = data.today_targets.completion_details;
            setDailyTargets({
              mcqs: { target: 10, completed: details.mcqs_completed || 0 },
              answers: { target: 1, completed: details.mains_attempted ? 1 : 0 },
              ca: { target: 1, completed: details.ca_reviewed ? 1 : 0 }
            });
          } else if (data.targets_report) {
            // Fallback to report data
            setDailyTargets({
              mcqs: { target: 10, completed: data.targets_report.tasks_completed >= 1 ? 10 : 0 },
              answers: { target: 1, completed: data.targets_report.tasks_completed >= 2 ? 1 : 0 },
              ca: { target: 1, completed: data.targets_report.tasks_completed >= 3 ? 1 : 0 }
            });
          }
        }
      } catch (err) {
        if (!isCancelError(err)) {
          console.error("Dashboard fetch error:", err);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAllData();
    
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);


  const getStatusColor = (status) => {
    switch (status) {
      case "safe": return { bg: "from-emerald-500 to-green-500", text: "text-emerald-600", border: "border-emerald-400" };
      case "borderline": return { bg: "from-amber-500 to-orange-500", text: "text-amber-600", border: "border-amber-400" };
      case "unsafe": return { bg: "from-red-500 to-rose-500", text: "text-red-600", border: "border-red-400" };
      default: return { bg: "from-slate-400 to-slate-500", text: "text-slate-600", border: "border-slate-300" };
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 60) return "text-emerald-600";
    if (prob >= 40) return "text-amber-600";
    return "text-red-600";
  };

  // Quick stats - using real data from APIs
  const stats = {
    questionsAttempted: intelligenceReport?.weekly_update?.questions_this_week || 0,
    accuracy: intelligenceReport?.selection_probability?.components?.test_accuracy || intelligenceReport?.weekly_update?.current_accuracy || 0,
    accuracyTrend: intelligenceReport?.weekly_update?.accuracy_change || 0,
    hoursThisWeek: Math.floor((intelligenceReport?.weekly_update?.tests_this_week || 0) * 0.5),
    streak: streakData.current_streak || 0,
    longestStreak: streakData.longest_streak || 0
  };

  // Explore features
  const exploreFeatures = [
    { title: "Weakness Map", icon: Target, path: "/weakness-map", color: "bg-gradient-to-br from-rose-500 to-pink-600", desc: "Your Blind Spots", upscOnly: true },
    { title: "Answer Arena", icon: PenTool, path: "/answer-arena", color: "bg-gradient-to-br from-indigo-600 to-purple-600", desc: "Daily Mains Practice", upscOnly: true },
    { title: "National Rank", icon: Trophy, path: "/national-benchmark", color: "bg-gradient-to-br from-amber-500 to-orange-500", desc: "Your All-India Rank", upscOnly: true },
    { title: "NCERT Foundation", icon: BookOpen, path: "/foundation", color: "bg-gradient-to-br from-emerald-500 to-teal-500", desc: "Class 6-12 Mastery" },
    { title: "Structured Tests", icon: Target, path: "/tests", color: "bg-gradient-to-br from-purple-500 to-indigo-500", desc: "Formal Assessments" },
    { title: "3-Year Roadmap", icon: Target, path: "/roadmap", color: "bg-gradient-to-br from-amber-500 to-orange-500", desc: "Your UPSC Journey", upscOnly: true },
    { title: "Daily Intelligence", icon: Newspaper, path: "/daily-intelligence", color: "bg-gradient-to-br from-rose-500 to-pink-500", desc: "Institute-Quality CA" },
    { title: "News Hub", icon: Rss, path: "/news-hub", color: "bg-gradient-to-br from-orange-500 to-red-500", desc: "Scraped News Feed" },
    { title: "Editorial Analyzer", icon: FileText, path: "/editorial-analyzer", color: "bg-gradient-to-br from-orange-500 to-amber-500", desc: "Analyze Editorials", upscOnly: true },
    { title: "Ask Doubts", icon: Brain, path: "/concept-intelligence", color: "bg-gradient-to-br from-cyan-500 to-blue-500", desc: "Concept Explainer", upscOnly: true },
    { title: "Clarity AI", icon: Sparkles, path: "/clarity-ai", color: "bg-gradient-to-br from-violet-500 to-purple-500", desc: "AI Mentor Chat" },
    { title: "All Features", icon: Zap, path: "/clarity", color: "bg-gradient-to-br from-amber-500 to-orange-500", desc: "Explore CLARIAS" },
    { title: "Intelligence Digests", icon: BarChart3, path: "/intelligence-digests", color: "bg-gradient-to-br from-indigo-500 to-blue-500", desc: "Weekly Reports" },
    { title: "Help & FAQ", icon: HelpCircle, path: "/help", color: "bg-gradient-to-br from-purple-500 to-fuchsia-500", desc: "Get Support" },
  ];

  // Focus topic
  const getFocusTopic = () => {
    if (commandStatus?.gs_status === "unsafe" || commandStatus?.gs_status === "borderline") {
      return { topic: "GS Foundation - Polity Basics", reason: "Strengthen your weak area", path: "/prelims/chapter-practice", urgency: "high" };
    }
    if (commandStatus?.csat_status === "unsafe" || commandStatus?.csat_status === "borderline") {
      return { topic: "CSAT - Reading Comprehension", reason: "Safe scoring zone", path: "/csat-suite", urgency: "high" };
    }
    return { topic: "Current Affairs - This Week", reason: "Stay updated with latest events", path: "/daily-intelligence", urgency: "normal" };
  };
  const focusTopic = getFocusTopic();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <Rocket className="w-6 h-6 text-indigo-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-indigo-300 font-medium">Loading your command center...</p>
        </div>
      </div>
    );
  }

  const command = commandStatus?.command;
  const selectionProb = intelligenceReport?.selection_probability?.probability || 35;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30" data-testid="dashboard-page">
      <PasswordSetupPrompt 
        open={showPasswordPrompt} 
        onClose={() => setShowPasswordPrompt(false)} 
      />

      {/* Parent link approval UI removed - now open access */}

      {/* ═══════════════════════════════════════════════════════════
          VIBRANT HEADER SECTION
          ═══════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          {/* Top Row */}
          <div className="flex items-start justify-between mb-4 gap-4">
            <div>
              <p className="text-indigo-200 text-sm flex items-center gap-1">
                {greeting.emoji} {greeting.text}, <span className="font-medium text-white">{firstName}</span>
              </p>
              <div className="flex items-center gap-2 mt-1">
                <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">
                  {isWhiteLabel ? getInstituteName() : "CLARIAS"}
                </h1>
                {isPro && (
                  <Badge className="bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0 font-semibold px-2 py-0.5">
                    <Crown className="w-3 h-3 mr-1" />
                    PRO
                  </Badge>
                )}
                {isWhiteLabel && (
                  <Badge className="bg-white/20 text-white/80 border-0 text-xs px-2 py-0.5">
                    Powered by CLARIAS
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Days Counter & Settings */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-2xl font-bold leading-none font-mono">{daysToExam}</p>
                <p className="text-[10px] text-indigo-200 mt-0.5">days to Prelims</p>
              </div>
              {!isWhiteLabel && (
                <a
                  href="https://t.me/clariasUPSC"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-9 w-9 grid place-items-center bg-[#0088cc] hover:bg-[#0077b5] rounded-xl transition-colors"
                  title="Join Telegram Community"
                  data-testid="telegram-btn"
                >
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </a>
              )}
              <button
                onClick={() => navigate("/refer")}
                className="h-9 w-9 grid place-items-center bg-amber-400/20 hover:bg-amber-400/30 rounded-xl transition-colors"
                title="Refer & Earn PRO"
                data-testid="refer-earn-nav-btn"
              >
                <Gift className="w-5 h-5 text-amber-300" />
              </button>
              <button
                onClick={() => navigate("/account-settings")}
                className="h-9 w-9 grid place-items-center bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Streak & Probability Row */}
          <div className="grid grid-cols-3 gap-3">
            {/* Selection Probability */}
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-xl p-3 cursor-pointer hover:bg-white/20 transition-all"
              onClick={() => navigate("/intelligence")}
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-300" />
                <span className="text-xs text-indigo-200">Selection Prob.</span>
              </div>
              <p className="text-xl font-bold font-mono">{selectionProb.toFixed(1)}%</p>
            </div>

            {/* Streak */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3" data-testid="dashboard-streak-card">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="w-4 h-4 text-orange-300" />
                <span className="text-xs text-indigo-200">Streak</span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <p className="text-xl font-bold font-mono">{stats.streak}</p>
                <span className="text-xs text-indigo-200">{stats.streak === 1 ? 'day' : 'days'}</span>
              </div>
            </div>

            {/* Accuracy */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-cyan-300" />
                <span className="text-xs text-indigo-200">Accuracy</span>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-xl font-bold font-mono">{stats.accuracy}%</p>
                {stats.accuracyTrend > 0 ? (
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                ) : stats.accuracyTrend < 0 ? (
                  <TrendingDown className="w-4 h-4 text-red-400" />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        
        {/* ═══════════════════════════════════════════════════════════
            UPGRADE TO PRO BANNER (FREE USERS ONLY)
            ═══════════════════════════════════════════════════════════ */}
        {!isPro && (
          <Card 
            className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 overflow-hidden cursor-pointer hover:shadow-lg transition-all"
            onClick={() => navigate(isSI ? "/upgrade?exam=si" : "/upgrade")}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-lg text-slate-800">Upgrade to CLARIAS {isSI ? "SI PRO" : "PRO"}</p>
                    {!isSI && (
                      <Badge className="bg-green-500 text-white border-0 text-xs font-bold">
                        83% OFF
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">
                    {isSI
                      ? "Full SI Mains Test Series, Aptitude engine, unlimited practice & analytics"
                      : "Unlimited MCQs, rubric evaluation, CA→MCQ converter & more"}
                  </p>
                </div>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 font-semibold"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Upgrade
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            MOTIVATIONAL QUOTE BANNER
            ═══════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-2xl p-4 border border-indigo-200/50">
          <p className="text-sm text-indigo-800 italic">"{currentQuote.text}"</p>
          <p className="text-xs text-indigo-600 mt-1">— {currentQuote.author}</p>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            PREP-OS STUDENT METRICS WIDGET
            ═══════════════════════════════════════════════════════════ */}
        <div data-testid="prep-os-widget-section">
          <PrepOSStudentWidget />
        </div>

        {/* ═══════════════════════════════════════════════════════════
            TODAY'S LEARNING TARGETS
            ═══════════════════════════════════════════════════════════ */}
        <Card className="border-0 shadow-lg bg-white overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Target className="w-4 h-4" />
                <span className="font-semibold text-sm">Today's Learning Targets</span>
              </div>
              <Badge className="bg-white/20 text-white border-0 text-xs">
                {dailyTargets.mcqs.completed + dailyTargets.answers.completed + dailyTargets.ca.completed}/
                {dailyTargets.mcqs.target + dailyTargets.answers.target + dailyTargets.ca.target} done
              </Badge>
            </div>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4">
              {/* MCQs Target */}
              <div 
                className="text-center p-3 bg-indigo-50 rounded-xl cursor-pointer hover:bg-indigo-100 transition-colors"
                onClick={() => navigate("/prelims/chapter-practice")}
              >
                <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-indigo-600">{dailyTargets.mcqs.completed}/{dailyTargets.mcqs.target}</p>
                <p className="text-xs text-slate-600">MCQs</p>
                <Progress value={(dailyTargets.mcqs.completed / dailyTargets.mcqs.target) * 100} className="h-1.5 mt-2" />
              </div>

              {/* Answers Target (UPSC) / Aptitude Target (SI) */}
              <div 
                className="text-center p-3 bg-purple-50 rounded-xl cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => navigate(isSI ? "/csat-suite" : "/mains")}
              >
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  {isSI ? <Brain className="w-5 h-5 text-white" /> : <PenTool className="w-5 h-5 text-white" />}
                </div>
                <p className="text-2xl font-bold text-purple-600">{dailyTargets.answers.completed}/{dailyTargets.answers.target}</p>
                <p className="text-xs text-slate-600">{isSI ? "Aptitude" : "Answers"}</p>
                <Progress value={(dailyTargets.answers.completed / dailyTargets.answers.target) * 100} className="h-1.5 mt-2" />
              </div>

              {/* CA Target */}
              <div 
                className="text-center p-3 bg-blue-50 rounded-xl cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => navigate("/daily-intelligence")}
              >
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Newspaper className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-blue-600">{dailyTargets.ca.completed}/{dailyTargets.ca.target}</p>
                <p className="text-xs text-slate-600">CA Read</p>
                <Progress value={(dailyTargets.ca.completed / dailyTargets.ca.target) * 100} className="h-1.5 mt-2" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            TODAY'S COMMAND (HERO CARD)
            ═══════════════════════════════════════════════════════════ */}
        <Card className={`border-0 shadow-xl overflow-hidden transform hover:scale-[1.01] transition-transform ${
          command?.priority === "critical" ? "bg-gradient-to-r from-red-500 via-red-600 to-rose-600" :
          command?.priority === "high" ? "bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" :
          command?.priority === "medium" ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" :
          "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"
        }`}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                command?.priority === "critical" || command?.priority === "high" 
                  ? "bg-white/20" 
                  : "bg-white/20"
              }`}>
                {command?.priority === "critical" || command?.priority === "high" ? (
                  <AlertTriangle className="w-7 h-7 text-white" />
                ) : (
                  <Rocket className="w-7 h-7 text-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white/80 text-xs font-medium mb-1 uppercase tracking-wide">Today, your best action is</p>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 leading-tight">
                  {command?.message || "✅ Your preparation is on track. Maintain consistency."}
                </h2>
                <Button
                  onClick={() => navigate(command?.cta_path || "/prelims")}
                  size="lg"
                  className="bg-white text-slate-800 hover:bg-slate-100 font-bold shadow-lg"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {command?.cta_text || "Daily Practice"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FUTURE BACKLOG: Command Center (PCE) - Uncomment when ready to deploy
        <Card 
          className="border-2 border-orange-300 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 cursor-pointer hover:shadow-2xl transition-all group overflow-hidden"
          onClick={() => navigate("/command-center")}
          data-testid="command-center-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white/80 text-xs font-medium mb-1 uppercase tracking-wide">Preparation Command Engine</p>
                <h3 className="text-lg font-bold text-white">Start Today's Session</h3>
                <p className="text-white/70 text-sm">Your daily orders are ready. No decisions, just action.</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-3 py-1 bg-white/20 rounded-full text-white text-sm font-bold">
                  {daysToExam} days left
                </div>
                <ChevronRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </CardContent>
        </Card>
        */}

        {/* ═══════════════════════════════════════════════════════════
            PRIMARY HUBS - PRELIMS & MAINS
            ═══════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 gap-4">
          {/* Prelims Hub */}
          <Card 
            className="border-2 border-indigo-200 bg-gradient-to-br from-white to-indigo-50 cursor-pointer hover:border-indigo-400 hover:shadow-xl transition-all group overflow-hidden"
            onClick={() => navigate("/prelims")}
          >
            <CardContent className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <p className="font-bold text-lg text-slate-800">Prelims Hub</p>
                <p className="text-xs text-slate-500 mb-3">MCQs & Practice</p>
                <div className="flex items-center gap-1 text-indigo-600 font-medium text-sm">
                  <span>Start Practice</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mains Hub (UPSC) / Aptitude & Reasoning Hub (SI) */}
          <Card 
            className="border-2 border-purple-200 bg-gradient-to-br from-white to-purple-50 cursor-pointer hover:border-purple-400 hover:shadow-xl transition-all group overflow-hidden"
            onClick={() => navigate(isSI ? "/csat-suite" : "/mains")}
          >
            <CardContent className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg">
                  {isSI ? <Brain className="w-8 h-8 text-white" /> : <PenTool className="w-8 h-8 text-white" />}
                </div>
                <p className="font-bold text-lg text-slate-800">{isSI ? "Aptitude Hub" : "Mains Hub"}</p>
                <p className="text-xs text-slate-500 mb-3">{isSI ? "Maths & Reasoning" : "Answer Writing"}</p>
                <div className="flex items-center gap-1 text-purple-600 font-medium text-sm">
                  <span>{isSI ? "Practice Now" : "Write Answers"}</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SI 180-DAY ROADMAP (SI track only)
            ═══════════════════════════════════════════════════════════ */}
        {isSI && (
          <Card
            className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50 cursor-pointer hover:border-teal-400 hover:shadow-xl transition-all group overflow-hidden"
            onClick={() => navigate("/si-roadmap")}
            data-testid="si-roadmap-tile"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg flex-shrink-0">
                  <Calendar className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-lg text-slate-800">180-Day SI Roadmap</p>
                    <Badge className="bg-teal-600 text-white border-0 text-[10px]">TS &amp; AP</Badge>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Day-by-day plan · today's tasks, one tap to practice</p>
                  <p className="text-xs text-teal-700 flex items-center gap-1"><Flame className="w-3 h-3" /> Build your streak</p>
                </div>
                <ChevronRight className="w-5 h-5 text-teal-600 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        )}

        {isSI && (
          <Card
            className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 cursor-pointer hover:border-amber-400 hover:shadow-xl transition-all group overflow-hidden"
            onClick={() => navigate("/csat-suite?mode=si-test-series")}
            data-testid="si-weekly-tile"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg flex-shrink-0">
                  <Trophy className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-lg text-slate-800">SI Mains Test Series</p>
                    <Badge className="bg-amber-600 text-white border-0 text-[10px]">NEW · Thu</Badge>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">26-week timed series · 200 Q, 180 min · behaviour report + leaderboard</p>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-600 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            TOPIC HEATMAP — per-chapter strengths & weak spots
            ═══════════════════════════════════════════════════════════ */}
        <TopicHeatmap />

        {/* ═══════════════════════════════════════════════════════════
            WEAK AREA ALERT (if applicable)
            ═══════════════════════════════════════════════════════════ */}
        {focusTopic.urgency === "high" && (
          <Card className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-amber-700 font-medium uppercase tracking-wide">Weak Area Detected</p>
                  <p className="font-bold text-slate-800">{focusTopic.topic}</p>
                  <p className="text-xs text-slate-600">{focusTopic.reason}</p>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => navigate(focusTopic.path)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
                >
                  Fix Now
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            RISK METERS - GS & CSAT
            ═══════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 gap-4">
          {/* GS Risk */}
          <Card 
            className={`border-2 cursor-pointer transition-all hover:shadow-lg ${
              getStatusColor(commandStatus?.gs_status).border
            } bg-white overflow-hidden`}
            onClick={() => navigate("/prelims")}
          >
            <div className={`h-1.5 bg-gradient-to-r ${getStatusColor(commandStatus?.gs_status).bg}`} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Target className={`w-5 h-5 ${getStatusColor(commandStatus?.gs_status).text}`} />
                    <span className="text-sm font-medium text-slate-700">GS Prelims</span>
                  </div>
                  <p className={`text-xl font-bold ${getStatusColor(commandStatus?.gs_status).text}`}>
                    {commandStatus?.gs_status === "safe" ? "🟢 SAFE" :
                     commandStatus?.gs_status === "borderline" ? "🟡 BORDERLINE" :
                     commandStatus?.gs_status === "unsafe" ? "🔴 UNSAFE" : "—"}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          {/* CSAT Risk */}
          <Card 
            className={`border-2 cursor-pointer transition-all hover:shadow-lg ${
              getStatusColor(commandStatus?.csat_status).border
            } bg-white overflow-hidden`}
            onClick={() => navigate("/csat-suite")}
          >
            <div className={`h-1.5 bg-gradient-to-r ${getStatusColor(commandStatus?.csat_status).bg}`} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className={`w-5 h-5 ${getStatusColor(commandStatus?.csat_status).text}`} />
                    <span className="text-sm font-medium text-slate-700">CSAT</span>
                  </div>
                  <p className={`text-xl font-bold ${getStatusColor(commandStatus?.csat_status).text}`}>
                    {commandStatus?.csat_status === "safe" ? "🟢 SAFE" :
                     commandStatus?.csat_status === "borderline" ? "🟡 BORDERLINE" :
                     commandStatus?.csat_status === "unsafe" ? "🔴 UNSAFE" : "—"}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            QUICK STATS ROW
            ═══════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats.questionsAttempted}</p>
              <p className="text-[10px] text-blue-200">Questions</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats.accuracy}%</p>
              <p className="text-[10px] text-emerald-200">Accuracy</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats.hoursThisWeek}h</p>
              <p className="text-[10px] text-purple-200">This Week</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white border-0">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">#{weeksToExam}</p>
              <p className="text-[10px] text-orange-200">Weeks Left</p>
            </CardContent>
          </Card>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            EXPLORE FEATURES GRID - Always visible
            ═══════════════════════════════════════════════════════════ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-slate-800">Explore Features</span>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {exploreFeatures.filter((f) => !isSI || !f.upscOnly).map((feature, idx) => (
              <Card 
                key={idx}
                className="border border-slate-200 cursor-pointer hover:shadow-lg hover:border-transparent transition-all group overflow-hidden"
                onClick={() => navigate(feature.path)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-sm text-slate-800">{feature.title}</p>
                  <p className="text-[10px] text-slate-500">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Footer Spacing */}
        <div className="h-8" />
        
        {/* Powered by CLARIAS - For White-Label Institutes */}
        <PoweredByBadge location="dashboard" className="pb-4" />
      </div>
    </div>
  );
};

export default DashboardPage;
